import { createDisposableDirective, useIntersectionObserver } from '@vueuse/core';
import type { Directive } from 'vue';

import {
  useLandingPageExperimentTracking,
  type LandingSectionImpressionAction,
} from '@/composables/tracking/useLandingPageExperimentTracking';

const IMPRESSION_THRESHOLD = 0.25;

export type SectionImpressionValue =
  | LandingSectionImpressionAction
  | {
      action: LandingSectionImpressionAction;
      label?: string;
    };

function parseImpressionValue(value: SectionImpressionValue) {
  return typeof value === 'string' ? { action: value } : value;
}

// `createDisposableDirective` runs `mounted` inside a per-element effect scope and stops it
// on unmount, which is what lets VueUse auto-dispose the observer. Without it no scope is
// active in a directive hook, so the observer would outlive the element.
export const sectionImpressionDirective: Directive<HTMLElement, SectionImpressionValue> =
  createDisposableDirective<HTMLElement, SectionImpressionValue>({
    mounted(el, { value }) {
      const { action, label } = parseImpressionValue(value);
      // Resolved per mount rather than at registration: the directive is installed at app
      // start, when the stores this composable reads are not usable yet.
      const { trackSectionImpression } = useLandingPageExperimentTracking();

      const { stop } = useIntersectionObserver(
        el,
        ([entry]) => {
          if (!entry?.isIntersecting) return;

          if (label) {
            trackSectionImpression(action, label);
          } else {
            trackSectionImpression(action);
          }
          // Impressions fire once; disconnect as soon as the section has been seen.
          stop();
        },
        { threshold: IMPRESSION_THRESHOLD },
      );
    },
  });
